// §4.2 STOCK UNIT CARD — floor variant, and the GUIDED CUT (spec 06 §3.2).
//
// §4.2's floor row: "action bar collapses to Cut / Re-measure / Move; numeric
// keypad for measure entry". Move is a §5-row-25 `repack` POST /movements —
// which is NOT a floor grant (§7 gives floor_worker no `inventory.adjust`) and
// is achieved on the floor by SCANNING the new bin (§3.5 rung 1, one motion,
// no form). So the floor bar is Cut · Re-measure, and relocation is the scan
// screen. That is recorded in the §4.2 row rather than left as a missing
// button.
//
// THE CUT IS THE HARD PART, and the two things that make it hard are both in
// the spec:
//
//  * §9.1 CONCURRENT CUTS. Two devices cut bobbin 1645 at once; the loser gets
//    409 `INSUFFICIENT_REMAINING` carrying the FRESH remaining. §9.1 spells out
//    the required behaviour verbatim: "Bobbin changed — remaining is now 1543m,
//    re-enter cut." So this screen NEVER retries a cut automatically. It shows
//    the fresh number, keeps the operator's piece list on screen so they can
//    adjust rather than retype, and makes them press the button again. The
//    outbox agrees: `parkedAdvice` classifies that code as "replan", so even
//    the tray refuses to offer a blind retry.
//  * OFFLINE, THE PLAN CANNOT BE VALIDATED. The tolerance comparison (§3.2 step
//    1) is exact integer cross-multiplication against a tenant setting the
//    device does not have, on a `remaining_measure` that may be stale. So the
//    screen does the ONE check it can do honestly — Σ pieces + kerf ≤ the
//    remaining it is showing — states which snapshot that remaining came from,
//    and lets the server be the authority. Reimplementing `planCut` in the
//    browser would be a second, weaker arithmetic on the ledger's core measure.
//
// Everything measure-shaped goes through the shared exact-decimal helpers
// (`lib/measure.ts`) — D-042: no quantity on this screen is ever a JS number.

import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  FloorButton,
  FloorError,
  FloorNeedsConnection,
  FloorNotSaved,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import { useFloorSubmit } from "../hooks/use-floor-outbox";
import { floorFeedback } from "../lib/floor-feedback";
import { readCacheKey } from "../lib/floor-read-cache";
import { floorFailureDetail, floorFailureMessage, floorRequestId } from "../lib/floor-errors";
import type { CutRequest, CutResponse } from "../lib/floor-ops";
import { useInventoryPermissions } from "../../office/inventory/hooks/permissions";
import {
  formatMeasure,
  formatMeasureWithUom,
  parseMeasure,
  typedMeasure,
} from "../../office/inventory/lib/measure";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";
import type { components } from "@assessly/api-client";
import { unitSymbol } from "../../../lib/quantity";
import { enumLabel } from "../../../lib/enum-label";

type StockUnit = components["schemas"]["StockUnit"];

const MEASURE_RE = /^\d{1,12}(\.\d{1,6})?$/u;

interface PieceDraft {
  readonly id: number;
  readonly length: string;
  readonly kind: "child" | "waste";
}

export function FloorUnitScreen() {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ from: "/floor/units/$id" });
  const perms = useInventoryPermissions();
  /**
   * Which panel owns the fixed action bar. §4.2's floor row puts the primary
   * action in the thumb arc; the cut used to be an inline button at the bottom
   * of a scrolling form, so the money screen — "scan → cut → done" — was the one
   * screen a one-handed operator could not finish one-handed.
   */
  const [panel, setPanel] = useState<"cut" | "remeasure">("cut");

  // Only the unit row is cached: §5 row 14's card also carries the active
  // reservation and the last five scans, and neither is a fact the floor should
  // show from a snapshot — a hold released ten minutes ago would still read as
  // live. What the floor needs offline is the identity and the measure.
  const read = useCachedRead<StockUnit>({
    queryKey: ["floor", "unit", id],
    cacheKey: readCacheKey("unit", id),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/stock-units/{id}", {
          params: { path: { id } },
          signal: floorReadSignal(signal),
        }),
        "stock-unit",
      ).unit,
  });

  const unit = read.data;

  return (
    <FloorScreen
      title={unit?.serial_code ?? t("inventory.floor.unit.title")}
      back={
        <Link to="/floor/stock" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      <SnapshotNote
        source={read.source}
        cachedAt={read.cachedAt}
        revalidating={read.revalidating}
      />

      {read.isLoading ? <FloorSkeleton rows={4} /> : null}

      {read.source === "none" && !read.isLoading ? (
        read.online ? (
          <FloorError
            title={t("inventory.floor.unit.notFoundTitle")}
            detail={t("inventory.floor.unit.notFoundBody")}
            requestId={floorRequestId(read.error)}
            onRetry={read.refetch}
          />
        ) : (
          <FloorNeedsConnection what={t("inventory.floor.unit.thisUnit")} />
        )
      ) : null}

      {unit !== undefined ? (
        <>
          <div className="floor-unit-head" data-testid="floor-unit-head">
            <p className="floor-unit-measure" data-testid="floor-unit-remaining">
              {formatMeasureWithUom(unit.remaining_measure, unit.measure_uom, {
                locale: i18n.language,
              })}
            </p>
            <p className="floor-unit-sub">
              {unit.capacity_measure !== null
                ? t("inventory.floor.unit.ofCapacity", {
                    capacity: formatMeasure(unit.capacity_measure, { locale: i18n.language }),
                    uom: unitSymbol(unit.measure_uom ?? "", { locale: i18n.language }),
                  })
                : t("inventory.floor.unit.noCapacity")}
            </p>
            <p className="floor-unit-sub">
              {unit.location_path ?? t("inventory.common.noLocation")}
            </p>
            <p className="floor-badge" data-testid="floor-unit-status">
              {enumLabel(t, "inventory.vocab.status", unit.status)}
            </p>
            {unit.is_offcut ? (
              <p className="floor-badge" data-testid="floor-unit-offcut">
                {t("inventory.floor.unit.offcut")}
              </p>
            ) : null}
          </div>

          {/* §4.2 floor: "action bar collapses to Cut / Re-measure". One panel
              is live at a time, so there is exactly one fixed bottom bar and
              the primary action is always inside the thumb arc.

              A MODE PAIR, NOT A TAB STRIP — corrected at UX-5. This wore
              `role="tablist"` around two plain `FloorButton`s: no `role="tab"`,
              no `aria-selected`, no arrow keys, no `tabpanel` on the thing it
              controlled. A screen reader announced a tab list containing zero
              tabs, which is worse than no ARIA at all, and the design-system
              gate's check 4 (one implementation of tab semantics, ever) is
              exactly the rule that exists to catch it.
              The honest shape is the one the floor ALREADY has for a mutually
              exclusive mode — D-081's issue/return toggle on the op-run screen:
              two glove-sized buttons carrying `aria-pressed`. That is also why
              this is not `NavTabs`: the office's tab register is a 36 px
              underline strip, and a 36 px target on a shop floor is not a
              target. The testids are unchanged (`M06-S13` taps
              `floor-unit-tab-remeasure` by testid, never by role). */}
          {/* F6: `aria-pressed` says which one is on; it does not say what the
              two of them are FOR. A group with an accessible name is what makes
              "Cut, pressed" announce as a choice rather than as a loose button
              — the one thing the (lying) tablist did give a screen reader. */}
          <div
            className="floor-modes floor-mode-toggle"
            role="group"
            aria-label={t("inventory.floor.unit.modeLabel")}
            data-testid="floor-unit-tabs"
          >
            <FloorButton
              variant={panel === "cut" ? "primary" : "default"}
              pressed={panel === "cut"}
              onClick={() => {
                setPanel("cut");
              }}
              testId="floor-unit-tab-cut"
            >
              {t("inventory.floor.cut.title")}
            </FloorButton>
            <FloorButton
              variant={panel === "remeasure" ? "primary" : "default"}
              pressed={panel === "remeasure"}
              onClick={() => {
                setPanel("remeasure");
              }}
              testId="floor-unit-tab-remeasure"
            >
              {t("inventory.floor.remeasure.title")}
            </FloorButton>
          </div>

          {panel === "cut" ? (
            perms.has("inventory.unit.cut") ? (
              // KEYED ON THE UNIT. Without this TanStack keeps one panel
              // instance across a `$id` change (the cut result links straight
              // to a child), so a plan typed for bobbin A would be submitted
              // against bobbin B — §4.2's "a wrong physical cut", the one
              // defect on this screen that cannot be undone with a keystroke.
              <CutPanel key={unit.id} unit={unit} stale={read.source === "cached"} />
            ) : (
              <p className="floor-hint" data-testid="floor-unit-nocut">
                {t("inventory.floor.unit.noCutPermission")}
              </p>
            )
          ) : perms.has("inventory.scan.locate") ? (
            <RemeasurePanel key={unit.id} unit={unit} />
          ) : (
            <p className="floor-hint" data-testid="floor-unit-noremeasure">
              {t("inventory.floor.remeasure.noPermission")}
            </p>
          )}
        </>
      ) : null}
    </FloorScreen>
  );
}

// ---------------------------------------------------------------------------
// Guided cut (§3.2)
// ---------------------------------------------------------------------------

function CutPanel({ unit, stale }: { unit: StockUnit; stale: boolean }) {
  const { t, i18n } = useTranslation();
  const { state, submit, reset } = useFloorSubmit();
  const [pieces, setPieces] = useState<PieceDraft[]>([{ id: 1, length: "", kind: "child" }]);
  const [kerf, setKerf] = useState("");
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  /** A §9.1 re-plan baseline: the remaining the SERVER just told us. */
  const [freshRemaining, setFreshRemaining] = useState<string | undefined>(undefined);
  /**
   * False after a cut has been committed (applied OR queued). Re-arming is an
   * explicit "plan another cut" tap, so the second press of a bouncing finger
   * cannot become a second physical cut.
   */
  const [armed, setArmed] = useState(true);

  const baseline = freshRemaining ?? unit.remaining_measure;

  const planned = useMemo(() => {
    // Exact integer arithmetic on the ×1e6 scale (D-042) — never a float sum of
    // cable lengths.
    let total = 0n;
    let malformed = false;
    for (const piece of pieces) {
      const text = typedMeasure(piece.length, i18n.language);
      if (text.length === 0) continue;
      if (!MEASURE_RE.test(text)) {
        malformed = true;
        continue;
      }
      total += parseMeasure(text) ?? 0n;
    }
    const kerfText = typedMeasure(kerf, i18n.language);
    if (kerfText.length > 0) {
      if (MEASURE_RE.test(kerfText)) {
        // §3.2: kerf is charged once per SEPARATION — n pieces consuming the
        // whole source need n−1 cuts. The screen shows the planner's own rule
        // rather than a per-piece guess.
        const separations = BigInt(
          Math.max(0, pieces.filter((p) => p.length.trim() !== "").length - 1),
        );
        total += (parseMeasure(kerfText) ?? 0n) * separations;
      } else {
        malformed = true;
      }
    }
    return { total, malformed };
  }, [pieces, kerf]);

  const remainingScaled = parseMeasure(baseline);
  const overRemaining =
    remainingScaled !== undefined && planned.total > remainingScaled && !planned.malformed;
  const anyPiece = pieces.some((piece) => piece.length.trim().length > 0);

  const settlement = state.phase === "settled" ? state.settlement : undefined;
  const applied =
    settlement?.outcome === "applied" ? (settlement.response as CutResponse) : undefined;

  async function performCut(): Promise<void> {
    setLocalError(undefined);
    if (planned.malformed) {
      setLocalError(t("inventory.floor.cut.malformed"));
      floorFeedback("error");
      return;
    }
    if (!anyPiece) {
      setLocalError(t("inventory.floor.cut.noPieces"));
      floorFeedback("error");
      return;
    }
    const body: CutRequest = {
      pieces: pieces
        .filter((piece) => piece.length.trim().length > 0)
        .map((piece) => ({ length: typedMeasure(piece.length, i18n.language), kind: piece.kind })),
      ...(kerf.trim().length > 0 ? { kerf: typedMeasure(kerf, i18n.language) } : {}),
    };
    const outcome = await submit({ kind: "cut", unitId: unit.id, body }, undefined, {
      identity: unit.serial_code,
    });
    if (outcome === undefined) {
      floorFeedback("error");
      return;
    }
    // A CUT IS NOT IDEMPOTENT: a second cut is a second physical cut, so the
    // queue deliberately never collapses two of them. That makes the form the
    // only guard against the classic dead-zone double-tap ("did that take?"),
    // and offline `enqueueAndWait` returns `queued` in milliseconds — so the
    // queued branch must disarm the form exactly as hard as the applied one.
    // Two hundred metres queued twice is four hundred metres removed from a
    // bobbin that never had them, permanently, in the ledger.
    if (outcome.outcome === "applied" || outcome.outcome === "queued") {
      setPieces([{ id: 1, length: "", kind: "child" }]);
      setKerf("");
      setFreshRemaining(undefined);
      setArmed(false);
      floorFeedback(outcome.outcome === "applied" ? "ok" : "warn");
    }
    if (outcome.outcome === "needs_attention") {
      // §9.1 verbatim: the fresh remaining rides the problem extensions so the
      // operator re-plans against the truth. The piece list stays on screen.
      const fresh = outcome.failure.extensions["remaining_measure"];
      if (fresh !== undefined) setFreshRemaining(fresh);
      floorFeedback("error");
    }
  }

  return (
    <section className="floor-panel" data-testid="floor-cut-panel">
      <h2 className="floor-panel-head">{t("inventory.floor.cut.title")}</h2>

      {stale ? (
        <p className="floor-hint floor-hint-warn" data-testid="floor-cut-stale">
          {t("inventory.floor.cut.staleBaseline")}
        </p>
      ) : null}

      {freshRemaining !== undefined ? (
        <p className="floor-replan" role="alert" data-testid="floor-cut-replan">
          {t("inventory.floor.cut.replan", {
            remaining: formatMeasure(freshRemaining, { locale: i18n.language }),
            uom: unitSymbol(unit.measure_uom ?? "", { locale: i18n.language }),
          })}
        </p>
      ) : null}

      <ul className="floor-pieces">
        {pieces.map((piece, index) => (
          <li key={piece.id} className="floor-piece">
            <label htmlFor={`piece-${String(piece.id)}`}>
              {t("inventory.floor.cut.pieceN", { n: index + 1 })}
            </label>
            <input
              id={`piece-${String(piece.id)}`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={piece.length}
              placeholder={t("inventory.floor.cut.piecePlaceholder")}
              onChange={(event) => {
                const next = event.target.value;
                setPieces((prev) =>
                  prev.map((p) => (p.id === piece.id ? { ...p, length: next } : p)),
                );
              }}
              data-testid={`floor-piece-${String(index)}`}
            />
            <select
              aria-label={t("inventory.floor.cut.pieceKind")}
              value={piece.kind}
              onChange={(event) => {
                const next = event.target.value === "waste" ? "waste" : "child";
                setPieces((prev) =>
                  prev.map((p) => (p.id === piece.id ? { ...p, kind: next } : p)),
                );
              }}
              data-testid={`floor-piece-kind-${String(index)}`}
            >
              <option value="child">{t("inventory.floor.cut.kindChild")}</option>
              <option value="waste">{t("inventory.floor.cut.kindWaste")}</option>
            </select>
            {pieces.length > 1 ? (
              <FloorButton
                variant="ghost"
                onClick={() => {
                  setPieces((prev) => prev.filter((p) => p.id !== piece.id));
                }}
              >
                {t("inventory.common.remove")}
              </FloorButton>
            ) : null}
          </li>
        ))}
      </ul>

      <FloorButton
        variant="ghost"
        onClick={() => {
          setPieces((prev) => [
            ...prev,
            { id: Math.max(0, ...prev.map((p) => p.id)) + 1, length: "", kind: "child" },
          ]);
        }}
        testId="floor-add-piece"
      >
        {t("inventory.floor.cut.addPiece")}
      </FloorButton>

      <div className="floor-field">
        <label htmlFor="floor-kerf">
          {t("inventory.floor.cut.kerf", {
            uom: unitSymbol(unit.measure_uom ?? "", { locale: i18n.language }),
          })}
        </label>
        <input
          id="floor-kerf"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={kerf}
          onChange={(event) => {
            setKerf(event.target.value);
          }}
          data-testid="floor-kerf"
        />
        <p className="floor-hint">{t("inventory.floor.cut.kerfHint")}</p>
      </div>

      <p className="floor-plan-summary" data-testid="floor-cut-summary">
        {t("inventory.floor.cut.summary", {
          planned: formatMeasure(scaledToText(planned.total), { locale: i18n.language }),
          remaining: formatMeasure(baseline, { locale: i18n.language }),
          uom: unitSymbol(unit.measure_uom ?? "", { locale: i18n.language }),
        })}
      </p>

      {overRemaining ? (
        <p className="floor-hint floor-hint-warn" data-testid="floor-cut-over">
          {t("inventory.floor.cut.overRemaining")}
        </p>
      ) : null}

      {localError !== undefined ? (
        <p className="floor-scan-error" role="alert" data-testid="floor-cut-local-error">
          {localError}
        </p>
      ) : null}

      {settlement?.outcome === "queued" ? (
        <p className="floor-result-queued" role="status" data-testid="floor-cut-queued">
          {t("inventory.floor.cut.queued")}
        </p>
      ) : null}

      {state.phase === "not_saved" ? (
        <FloorNotSaved
          onRetry={() => {
            reset();
            setArmed(true);
          }}
        />
      ) : null}

      {settlement?.outcome === "needs_attention" ? (
        <FloorError
          title={t("inventory.floor.cut.failedTitle")}
          detail={[
            floorFailureMessage(t, settlement.failure),
            floorFailureDetail(t, settlement.failure, i18n.language),
          ]
            .filter((line): line is string => line !== undefined)
            .join(" ")}
          requestId={settlement.failure.requestId}
        />
      ) : null}

      {applied !== undefined ? (
        <div
          className="floor-result-card floor-result-ok"
          role="status"
          data-testid="floor-cut-done"
        >
          <p className="floor-result-title">{t("inventory.floor.cut.doneTitle")}</p>
          <ul className="floor-children">
            {applied.children.map((child) => (
              <li key={child.id}>
                <Link to="/floor/units/$id" params={{ id: child.id }}>
                  {child.serial_code}
                </Link>{" "}
                ·{" "}
                {formatMeasureWithUom(child.remaining_measure, unit.measure_uom, {
                  locale: i18n.language,
                })}
                {child.is_offcut ? ` · ${t("inventory.floor.unit.offcut")}` : ""}
                {child.quarantined ? ` · ${t("inventory.vocab.qc.quarantined")}` : ""}
              </li>
            ))}
          </ul>
          <p className="floor-result-body">
            {t("inventory.floor.cut.parentNow", {
              remaining: formatMeasure(applied.parent_remaining_measure, {
                locale: i18n.language,
              }),
              uom: unitSymbol(unit.measure_uom ?? "", { locale: i18n.language }),
            })}
          </p>
          {applied.reservation_not_covered === true ? (
            <p className="floor-result-body floor-hint-warn" data-testid="floor-cut-reservation">
              {t("inventory.floor.cut.reservationNotCovered")}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* The fixed bottom bar. `.floor-actionbar` pins it inside the thumb arc,
          and its label carries the plan so the operator confirms the numbers at
          the moment of the tap rather than from a summary scrolled off-screen. */}
      <div className="floor-actionbar">
        {armed ? (
          <FloorButton
            variant="primary"
            disabled={state.phase === "submitting" || !anyPiece}
            onClick={() => {
              void performCut();
            }}
            testId="floor-cut-submit"
          >
            {state.phase === "submitting"
              ? t("inventory.floor.cut.submitting")
              : t("inventory.floor.cut.submitWithPlan", {
                  pieces: pieces.filter((p) => p.length.trim().length > 0).length,
                  planned: formatMeasure(scaledToText(planned.total), { locale: i18n.language }),
                  remaining: formatMeasure(baseline, { locale: i18n.language }),
                  uom: unitSymbol(unit.measure_uom ?? "", { locale: i18n.language }),
                })}
          </FloorButton>
        ) : (
          <FloorButton
            variant="default"
            onClick={() => {
              reset();
              setArmed(true);
            }}
            testId="floor-cut-rearm"
          >
            {t("inventory.floor.cut.planAnother")}
          </FloorButton>
        )}
      </div>
    </section>
  );
}

/** Render a ×1e6 scaled BigInt back as exact decimal text (never via Number). */
function scaledToText(scaled: bigint): string {
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const whole = abs / 1_000_000n;
  const frac = (abs % 1_000_000n).toString().padStart(6, "0");
  return `${negative ? "-" : ""}${whole.toString()}.${frac}`;
}

// ---------------------------------------------------------------------------
// Re-measure (§3.5's `entered_measure` path, §5 row 17)
// ---------------------------------------------------------------------------

function RemeasurePanel({ unit }: { unit: StockUnit }) {
  const { t, i18n } = useTranslation();
  const { state, submit, reset } = useFloorSubmit();
  const [value, setValue] = useState("");
  const [invalid, setInvalid] = useState(false);

  const settlement = state.phase === "settled" ? state.settlement : undefined;
  const applied =
    settlement?.outcome === "applied"
      ? (settlement.response as components["schemas"]["RemeasureResponse"])
      : undefined;

  return (
    <section className="floor-panel" data-testid="floor-remeasure-panel">
      <h2 className="floor-panel-head">{t("inventory.floor.remeasure.title")}</h2>
      <div className="floor-field">
        <label htmlFor="floor-remeasure">
          {t("inventory.floor.remeasure.label", {
            uom: unitSymbol(unit.measure_uom ?? "", { locale: i18n.language }),
          })}
        </label>
        <input
          id="floor-remeasure"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setInvalid(false);
          }}
          data-testid="floor-remeasure-input"
        />
      </div>
      {invalid ? (
        <p className="floor-scan-error" role="alert" data-testid="floor-remeasure-invalid">
          {t("inventory.floor.scan.measureInvalid")}
        </p>
      ) : null}

      {settlement?.outcome === "queued" ? (
        <p className="floor-result-queued" role="status" data-testid="floor-remeasure-queued">
          {t("inventory.floor.remeasure.queued")}
        </p>
      ) : null}
      {settlement?.outcome === "needs_attention" ? (
        <FloorError
          title={t("inventory.floor.remeasure.failedTitle")}
          detail={floorFailureMessage(t, settlement.failure)}
          requestId={settlement.failure.requestId}
        />
      ) : null}
      {state.phase === "not_saved" ? <FloorNotSaved onRetry={reset} /> : null}
      {applied !== undefined ? (
        <p className="floor-result-body" role="status" data-testid="floor-remeasure-done">
          {applied.adjusted
            ? t("inventory.floor.remeasure.adjusted", {
                delta: applied.delta,
                remaining: formatMeasure(applied.remaining_measure, { locale: i18n.language }),
              })
            : t("inventory.floor.remeasure.withinTolerance", {
                remaining: formatMeasure(applied.remaining_measure, { locale: i18n.language }),
              })}
        </p>
      ) : null}

      <div className="floor-actionbar">
        <FloorButton
          variant="primary"
          disabled={state.phase === "submitting" || value.trim().length === 0}
          onClick={() => {
            const text = typedMeasure(value, i18n.language);
            if (!MEASURE_RE.test(text)) {
              setInvalid(true);
              floorFeedback("error");
              return;
            }
            reset();
            void submit(
              { kind: "remeasure", unitId: unit.id, body: { entered_measure: text } },
              undefined,
              { identity: unit.serial_code },
            ).then((settled) => {
              if (settled === undefined) {
                floorFeedback("error");
                return;
              }
              floorFeedback(settled.outcome === "applied" ? "ok" : "warn");
              setValue("");
            });
          }}
          testId="floor-remeasure-submit"
        >
          {t("inventory.floor.remeasure.submit")}
        </FloorButton>
      </div>
    </section>
  );
}
